import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, CircularProgress, Slide, DialogContent, DialogTitle, Dialog, DialogActions, Stack, Link, Box, IconButton, Tooltip } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CreateIcon from '@mui/icons-material/Create';

import { RootState } from '../../../reducers';
import classes from '../Styles';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import {
    generateAssessmentWorkflowPDF,
    getAssessmentWorkflowHistory,
    reopenEngagement,
    resetDownloadVerificationModal,
} from '../../../store/documentEngagement.slice';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { PartTwoDownloadEnum } from '../../../Enum/PartTwoDownloadEnum';
import Authorization from '../../../utils/Authorization';
import { useNavigate } from 'react-router-dom';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
    className?: string;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`history-tabpanel-${index}`} aria-labelledby={`history-tab-${index}`} {...other}>
            {value === index && children}
        </div>
    );
}

enum AssessmentHistoryMenu {
    History = 1,
    Draft = 2,
}

const AssessmentHistory: React.FC<{ memberId: string; open: boolean; onClose: Function; canShowDrafts: boolean }> = ({
    memberId,
    open,
    onClose,
    canShowDrafts,
}) => {
    const [tabValue, setTabValue] = useState(AssessmentHistoryMenu.History);
    const [selectedHistoryId, setSelectedHistoryId] = useState<string>('');
    const [engagementToReopen, setEngagementToReopen] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const authData = new Authorization();
    const canAddEditEngagements = authData.isInPermission('Add / Edit engagements');
    const { assessmentHistoryPreviews, downloadVerificationModal, generatingPdfEngagementIds, isFetchingHistory, isGeneratingPDF, isSaving } = useSelector(
        (state: RootState) => state.documentEngagement
    );

    useEffect(() => {
        if (open) {
            setTabValue(AssessmentHistoryMenu.History);
            getAssessmentHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const getAssessmentHistory = () => {
        dispatch(getAssessmentWorkflowHistory(memberId));
    };

    const handleOnSelectHistory = (engagementId: string) => {
        setSelectedHistoryId(engagementId);
        dispatch(generateAssessmentWorkflowPDF(engagementId));
    };

    const handleOnMedicationModal = () => onClose();

    const handleConfirmReopen = () => {
        if (!engagementToReopen) return;

        dispatch(
            reopenEngagement(engagementToReopen, navigate, `assessment/${engagementToReopen}`, () => {
                setEngagementToReopen(null);
                onClose();
            })
        );
    };

    return (
        <Dialog
            disableScrollLock
            disableRestoreFocus
            disableAutoFocus
            disableEnforceFocus
            open={open}
            maxWidth="xl"
            sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleOnMedicationModal}
        >
            <DialogTitle>History</DialogTitle>
            <DialogContent dividers sx={{ width: '600px', padding: 0 }}>
                <Stack direction="row">
                    <Box sx={classes.historyList}>
                        <TabPanel value={tabValue} index={AssessmentHistoryMenu.History}>
                            {isFetchingHistory ? (
                                <Box display="flex" justifyContent="center" sx={classes.loadingContainer}>
                                    <CircularProgress />
                                </Box>
                            ) : Boolean(assessmentHistoryPreviews.length) ? (
                                assessmentHistoryPreviews.map((history, index) => (
                                    <Stack key={`saved-draft${history.Id}`} direction="row" spacing={0} alignItems="center" sx={classes.historyItem}>
                                        <PictureAsPdfIcon color="primary" sx={{ pr: 1 }} />
                                        <Link underline="none" onClick={() => handleOnSelectHistory(history.Id)}>
                                            {`${(() => {
                                                const d = toDate(history?.CreatedDate);
                                                return d ? formatDate(d, 'MMMM dd, yyyy hh:mm a') : '';
                                            })()} - ${history.Provider}`}
                                        </Link>
                                        {canAddEditEngagements && authData.UserId === history.ProviderId ? (
                                            <Tooltip
                                                title={
                                                    history.CanReopen
                                                        ? 'Edit'
                                                        : 'Only engagements with all encounters For Review and no submitted claim can be reopened.'
                                                }
                                                placement="left"
                                            >
                                                <span>
                                                    <IconButton
                                                        aria-label="Edit engagement"
                                                        disabled={!history.CanReopen}
                                                        onClick={() => setEngagementToReopen(history.Id)}
                                                    >
                                                        <CreateIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                        {selectedHistoryId === history.Id && isGeneratingPDF ? <CircularProgress size={16} sx={{ ml: 2 }} /> : null}
                                    </Stack>
                                ))
                            ) : (
                                <div className="no-data-container">No History found</div>
                            )}
                        </TabPanel>
                    </Box>
                    <AlertDialog
                        open={downloadVerificationModal?.Show}
                        title="Part 2 consent verification"
                        showTop
                        isLoading={generatingPdfEngagementIds.some((id) => id === downloadVerificationModal.EngagementId)}
                        okText={String(PartTwoDownloadEnum.WithPartTwoDataDescription)}
                        skipText={String(PartTwoDownloadEnum.WithoutPartTwoDataDescription)}
                        cancelText="Cancel"
                        buttonsDirection="column-reverse"
                        skipButtonVariant="contained"
                        onConfirm={() => dispatch(resetDownloadVerificationModal({ ...downloadVerificationModal, Show: false, ShowVerificationOptions: true }))}
                        onSkip={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.WithoutPartTwoData))}
                        onClose={() => dispatch(resetDownloadVerificationModal())}
                        message={'This assessment contains Part 2 data, which cannot be shared without appropriate Part 2 consent. Do you want to'}
                    />
                    <AlertDialog
                        open={downloadVerificationModal?.ShowVerificationOptions}
                        title="Part 2 consent verification"
                        showTop
                        isLoading={generatingPdfEngagementIds.some((id) => id === downloadVerificationModal.EngagementId)}
                        okText={String(PartTwoDownloadEnum.VerifiedDescription)}
                        skipText={String(PartTwoDownloadEnum.InternalUseDescription)}
                        cancelText={String(PartTwoDownloadEnum.NoneDescription)}
                        buttonsDirection="column-reverse"
                        skipButtonVariant="contained"
                        onConfirm={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.Verified))}
                        onSkip={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.InternalUse))}
                        onClose={() => dispatch(resetDownloadVerificationModal())}
                        message={downloadVerificationModal?.Message}
                    />
                    <AlertDialog
                        open={engagementToReopen !== null}
                        title="Move assessment to drafts?"
                        message="This saved-for-review assessment will be moved to Draft. It will no longer appear in Document Engagements or Claims Management while you edit it. Continue?"
                        okText="Move to Drafts and Edit"
                        isLoading={isSaving}
                        onConfirm={handleConfirmReopen}
                        onClose={() => setEngagementToReopen(null)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button size="small" type="button" onClick={handleOnMedicationModal} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssessmentHistory;
